import React, { useState } from "react";
import { Check, Shield, Sparkles, Crown, ArrowRight, Heart, PhoneCall, GraduationCap, Stethoscope, Home, BookOpen, Quote } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import ApplicationDialog from "../components/ApplicationDialog";
import { useLang } from "../i18n/LangContext";

const NAVY = "#1B2A4A";
const GOLD = "#C9A96E";
const CREAM = "#FAF8F5";

const I18N = {
  uk: {
    kicker: "СУПРОВІД СТУДЕНТА",
    h1: "Пакети супроводу під час навчання",
    sub: "Індивідуальний супровід на кожному етапі навчання за кордоном — від щоденної підтримки до преміального індивідуального куратора.",
    cta: "Записатися на консультацію",
    year: "/ рік",
    fullBachelor: "/ на весь бакалаврат",
    included: "Що входить:",
    guarantee: "Всі пакети продовжуються щорічно · Оплата в рік або одноразово за весь бакалаврат",
    // Value proposition
    valueEyebrow: "ЧОМУ БАТЬКИ ОБИРАЮТЬ СУПРОВІД",
    valueTitle: "Ваша дитина за кордоном — не сама",
    valueSub: "Ви за 2000+ км від дитини. Ми — за 5 хвилин. Тримаємо руку на пульсі 24/7, щоб ви спали спокійно, а дитина мала до кого звернутися в будь-якій ситуації.",
    // Situations
    situationsEyebrow: "6 СИТУАЦІЙ, КОЛИ СУПРОВІД РЯТУЄ",
    situationsTitle: "Реальні кейси наших студентів",
    situations: [
      { icon: Stethoscope, t: "Дитина захворіла о 2 ночі", d: "Викликаємо лікаря, супроводжуємо до клініки, вирішуємо питання зі страховкою — поки ви спите." },
      { icon: BookOpen, t: "Не встигає з навчанням", d: "За 24 години знаходимо репетитора, домовляємось з викладачем, будуємо план порятунку семестру." },
      { icon: PhoneCall, t: "Загубила паспорт або документи", d: "Ведемо до консульства, готуємо всі документи, робимо термінове відновлення без паніки." },
      { icon: Home, t: "Проблеми з гуртожитком або орендою", d: "Розв'язуємо конфлікт з lending-компанією, шукаємо нове житло, супроводжуємо переїзд." },
      { icon: GraduationCap, t: "Загроза відрахування", d: "Ведемо переговори з деканатом, готуємо документи, організовуємо переведення в інший заклад." },
      { icon: Heart, t: "Стрес та адаптація", d: "Психологічна підтримка, культурна орієнтація, зустрічі з іншими студентами STUDYGRI." }
    ],
    // Testimonial
    testimonialQuote: "«Донька навчається в Нідерландах уже 2 роки. Кожен раз, коли щось не так — я пишу STUDYGRI і за годину все вирішено. Це найкраща інвестиція в наш спокій.»",
    testimonialAuthor: "Ірина, мама Дарини",
    testimonialContext: "University of Amsterdam · Пакет Стандарт",
    packages: [
      {
        name: "Базовий", price: "€1 200", period: "/ рік", icon: Shield,
        desc: "Основна підтримка студента + інформування батьків.",
        features: [
          "Звіти протягом року батькам (академічні результати, прогрес)",
          "Консультації з адміністративних питань (віза, продовження документів)",
          "Екстрена комунікація 24/7",
          "Рекомендації базових сервісів (таксі, магазин, лікарня, аптека)"
        ]
      },
      {
        name: "Стандарт", price: "€1 700", period: "/ рік", icon: Sparkles,
        highlight: true,
        desc: "Комплексна підтримка — академіка, побут, медицина, адаптація.",
        features: [
          "Все з пакету «Базовий»",
          "Повна допомога в побутових питаннях (бронювання авіаквитків, готелів для батьків)",
          "Пошук репетиторів, індивідуальні консультації при низьких оцінках",
          "Підтримка у випадку відрахування (переведення, трансфер)",
          "Організація медичних послуг (запис до лікаря, страхові питання)",
          "Супровід при адаптації (культура, побут, місцеві сервіси)"
        ]
      },
      {
        name: "Преміум", price: "€3 500", period: "/ на весь бакалаврат", icon: Crown,
        desc: "Ексклюзивний супровід з персональним куратором на весь період навчання.",
        features: [
          "Все з пакету «Стандарт»",
          "Персональний куратор для студента + менеджер для батьків",
          "Індивідуальні онлайн-зустрічі за запитом",
          "Розширені звіти: академічний прогрес + соціальна адаптація",
          "Допомога в подовженні/зміні віз",
          "Організація приїздів батьків (поїздки, трансфери)",
          "Академічна «швидка допомога» — інтенсивний пошук репетиторів",
          "За потреби: стажування, літні програми, кар'єрні консультації"
        ]
      }
    ]
  },
  ru: {
    kicker: "СОПРОВОЖДЕНИЕ СТУДЕНТА",
    h1: "Пакеты сопровождения во время учёбы",
    sub: "Индивидуальное сопровождение на каждом этапе учёбы за границей — от ежедневной поддержки до премиального персонального куратора.",
    cta: "Записаться на консультацию",
    year: "/ год", fullBachelor: "/ на весь бакалавриат",
    included: "Что входит:",
    guarantee: "Все пакеты продлеваются ежегодно · Оплата в год или единоразово за весь бакалавриат",
    valueEyebrow: "ПОЧЕМУ РОДИТЕЛИ ВЫБИРАЮТ СОПРОВОЖДЕНИЕ",
    valueTitle: "Ваш ребёнок за границей — не один",
    valueSub: "Вы за 2000+ км от ребёнка. Мы — за 5 минут. Держим руку на пульсе 24/7, чтобы вы спали спокойно, а ребёнок мог обратиться в любой ситуации.",
    situationsEyebrow: "6 СИТУАЦИЙ, КОГДА СОПРОВОЖДЕНИЕ СПАСАЕТ",
    situationsTitle: "Реальные кейсы наших студентов",
    situations: [
      { icon: Stethoscope, t: "Ребёнок заболел в 2 ночи", d: "Вызываем врача, сопровождаем в клинику, решаем вопросы со страховкой — пока вы спите." },
      { icon: BookOpen, t: "Не успевает с учёбой", d: "За 24 часа находим репетитора, договариваемся с преподавателем, строим план спасения семестра." },
      { icon: PhoneCall, t: "Потерял паспорт или документы", d: "Ведём в консульство, готовим все документы, делаем срочное восстановление без паники." },
      { icon: Home, t: "Проблемы с общежитием или арендой", d: "Решаем конфликт с lending-компанией, ищем новое жильё, сопровождаем переезд." },
      { icon: GraduationCap, t: "Угроза отчисления", d: "Ведём переговоры с деканатом, готовим документы, организуем перевод в другое заведение." },
      { icon: Heart, t: "Стресс и адаптация", d: "Психологическая поддержка, культурная ориентация, встречи с другими студентами STUDYGRI." }
    ],
    testimonialQuote: "«Дочка учится в Нидерландах уже 2 года. Каждый раз, когда что-то не так — пишу STUDYGRI и через час всё решено. Это лучшая инвестиция в наше спокойствие.»",
    testimonialAuthor: "Ирина, мама Дарины",
    testimonialContext: "University of Amsterdam · Пакет Стандарт",
    packages: [
      { name: "Базовый", price: "€1 200", period: "/ год", icon: Shield,
        desc: "Основная поддержка студента + информирование родителей.",
        features: ["Отчёты в течение года родителям (академические результаты, прогресс)","Консультации по административным вопросам (виза, документы)","Экстренная связь 24/7","Рекомендации базовых сервисов (такси, магазин, больница, аптека)"] },
      { name: "Стандарт", price: "€1 700", period: "/ год", icon: Sparkles, highlight: true,
        desc: "Комплексная поддержка — академика, быт, медицина, адаптация.",
        features: ["Всё из пакета «Базовый»","Полная помощь в бытовых вопросах (билеты, отели для родителей)","Поиск репетиторов, консультации при низких оценках","Поддержка при отчислении (переводы, трансфер)","Организация медицинских услуг (запись к врачу, страховка)","Сопровождение при адаптации (культура, быт, сервисы)"] },
      { name: "Премиум", price: "€3 500", period: "/ на весь бакалавриат", icon: Crown,
        desc: "Эксклюзивное сопровождение с персональным куратором на весь период учёбы.",
        features: ["Всё из пакета «Стандарт»","Персональный куратор + менеджер для родителей","Индивидуальные онлайн-встречи по запросу","Расширенные отчёты: академика + социальная адаптация","Помощь в продлении/смене виз","Организация приездов родителей (поездки, трансферы)","Академическая «скорая помощь» — интенсивный поиск репетиторов","При необходимости: стажировки, летние программы, карьерные консультации"] }
    ]
  },
  en: {
    kicker: "STUDENT SUPPORT",
    h1: "Support packages during studies",
    sub: "Personalized support at every stage of studying abroad — from daily assistance to a premium dedicated curator.",
    cta: "Book a consultation",
    year: "/ year", fullBachelor: "/ full bachelor",
    included: "What's included:",
    guarantee: "All packages renew annually · Pay yearly or one-off for the entire bachelor",
    valueEyebrow: "WHY PARENTS CHOOSE ONGOING SUPPORT",
    valueTitle: "Your child abroad — not alone",
    valueSub: "You're 2000+ km away. We're 5 minutes away. We keep our finger on the pulse 24/7 so you can sleep peacefully and your child always has someone to turn to.",
    situationsEyebrow: "6 SITUATIONS WHERE SUPPORT SAVES THE DAY",
    situationsTitle: "Real cases from our students",
    situations: [
      { icon: Stethoscope, t: "Child gets sick at 2 AM", d: "We call the doctor, accompany to the clinic, handle insurance — while you sleep." },
      { icon: BookOpen, t: "Falling behind in studies", d: "Within 24 hours we find a tutor, talk to the professor, build a semester rescue plan." },
      { icon: PhoneCall, t: "Lost passport or documents", d: "We take them to the consulate, prepare all documents, handle urgent renewal without panic." },
      { icon: Home, t: "Housing or rental problems", d: "We resolve conflicts with landlords, find new housing, arrange the move." },
      { icon: GraduationCap, t: "Threat of expulsion", d: "We negotiate with the dean's office, prepare documents, arrange transfer to another institution." },
      { icon: Heart, t: "Stress and adaptation", d: "Psychological support, cultural orientation, meetings with other STUDYGRI students." }
    ],
    testimonialQuote: "\u201cMy daughter has been studying in the Netherlands for 2 years. Every time something goes wrong — I message STUDYGRI and it's solved within an hour. Best investment in our peace of mind.\u201d",
    testimonialAuthor: "Iryna, mom of Daryna",
    testimonialContext: "University of Amsterdam · Standard package",
    packages: [
      { name: "Basic", price: "€1,200", period: "/ year", icon: Shield,
        desc: "Core student support + regular parent updates.",
        features: ["Regular reports to parents (academic results, progress)","Consultations on administrative issues (visa, document renewals)","24/7 emergency communication","Recommendations for basic services (taxi, shop, hospital, pharmacy)"] },
      { name: "Standard", price: "€1,700", period: "/ year", icon: Sparkles, highlight: true,
        desc: "Full support — academic, daily life, medical, adaptation.",
        features: ["Everything in Basic","Full help with everyday matters (flights, hotels for parents)","Tutor search, personal consultations for low grades","Support in case of expulsion (transfer to another institution)","Medical service arrangement (doctor appointments, insurance)","Adaptation support (culture, daily life, local services)"] },
      { name: "Premium", price: "€3,500", period: "/ full bachelor", icon: Crown,
        desc: "Exclusive support with a dedicated curator throughout the entire studies.",
        features: ["Everything in Standard","Personal curator for the student + manager for parents","One-on-one online meetings on demand","Extended reports: academic progress + social adaptation","Assistance with visa extension/change","Organization of parents' visits (trips, transfers)","Academic emergency help — intensive tutor search","On request: internships, summer programs, career consultations"] }
    ]
  }
};

export default function StudentSupportPage() {
  const { lang } = useLang();
  const t = I18N[lang] || I18N.en;
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <Header onOpenForm={() => setFormOpen(true)} />
      <main className="pt-28 md:pt-36 pb-20" data-testid="student-support-page">
        {/* HERO */}
        <section className="relative overflow-hidden pb-14 md:pb-20 px-5 md:px-8" style={{ background: NAVY, color: '#fff' }}>
          <div className="max-w-5xl mx-auto text-center pt-14 md:pt-20">
            <div className="inline-block text-[11px] font-bold tracking-[0.28em] uppercase mb-5 px-4 py-1.5 rounded-full" style={{ background: GOLD, color: NAVY }}>{t.kicker}</div>
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.1] text-balance" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>{t.h1}</h1>
            <p className="mt-6 text-base md:text-xl max-w-3xl mx-auto" style={{ color: 'rgba(255,255,255,0.85)' }}>{t.sub}</p>
            <button onClick={() => setFormOpen(true)} data-testid="ss-hero-cta" className="mt-9 inline-flex items-center gap-2 px-8 h-13 py-3.5 rounded-full font-bold text-base transition hover:opacity-90" style={{ background: GOLD, color: NAVY }}>
              {t.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>

        {/* VALUE HEADLINE + IMAGE */}
        <section className="max-w-6xl mx-auto px-5 md:px-8 mt-16 md:mt-20">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="text-[11px] font-bold tracking-[0.28em] uppercase mb-3" style={{ color: GOLD }}>{t.valueEyebrow}</div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] mb-5" style={{ color: NAVY, fontWeight: 900, letterSpacing: '-0.025em' }}>{t.valueTitle}</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: '#444' }}>{t.valueSub}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold" style={{ background: '#fff', color: NAVY, border: `1.5px solid ${GOLD}` }}>⏱ 24/7 підтримка</span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold" style={{ background: '#fff', color: NAVY, border: `1.5px solid ${GOLD}` }}>📍 Локальна команда</span>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold" style={{ background: '#fff', color: NAVY, border: `1.5px solid ${GOLD}` }}>💬 Прямий контакт</span>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3]" style={{ boxShadow: '0 30px 80px -30px rgba(27,42,74,0.35)', border: `3px solid ${GOLD}` }}>
                <img src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=900&q=80" alt="Student support" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-4 -left-4 md:-left-6 rounded-2xl px-5 py-3 font-bold text-sm" style={{ background: NAVY, color: GOLD, boxShadow: '0 12px 40px -12px rgba(27,42,74,0.4)' }}>
                💛 5 хв середній час відповіді
              </div>
            </div>
          </div>
        </section>

        {/* SITUATIONS — 6 real-life scenarios */}
        <section className="max-w-6xl mx-auto px-5 md:px-8 mt-20 md:mt-24">
          <div className="text-center mb-10">
            <div className="text-[11px] font-bold tracking-[0.28em] uppercase mb-3" style={{ color: GOLD }}>{t.situationsEyebrow}</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1]" style={{ color: NAVY, fontWeight: 900, letterSpacing: '-0.025em' }}>{t.situationsTitle}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(t.situations || []).map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="rounded-2xl p-6 transition hover:-translate-y-1" style={{ background: '#fff', border: '1px solid #E8E0D5', boxShadow: '0 8px 24px -14px rgba(27,42,74,0.15)' }} data-testid={`ss-situation-${i}`}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(201,169,110,0.15)' }}>
                    <Icon className="w-6 h-6" style={{ color: GOLD }} strokeWidth={1.8} />
                  </div>
                  <div className="font-display text-lg mb-2 leading-tight" style={{ color: NAVY, fontWeight: 900 }}>{s.t}</div>
                  <div className="text-sm leading-relaxed" style={{ color: '#666' }}>{s.d}</div>
                </div>
              );
            })}
          </div>
        </section>

        {/* TESTIMONIAL */}
        <section className="max-w-4xl mx-auto px-5 md:px-8 mt-20 md:mt-24">
          <div className="rounded-3xl p-8 md:p-12 relative overflow-hidden" style={{ background: NAVY, color: '#fff' }}>
            <Quote className="absolute -top-2 -left-2 md:top-6 md:left-6 w-24 h-24 md:w-28 md:h-28" style={{ color: 'rgba(201,169,110,0.12)' }} strokeWidth={1} />
            <div className="relative">
              <p className="font-display text-xl md:text-2xl lg:text-3xl leading-relaxed mb-6" style={{ fontFamily: "'Georgia', 'Forum', serif", fontWeight: 400, letterSpacing: '-0.01em' }}>
                {t.testimonialQuote}
              </p>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden shrink-0" style={{ border: `2px solid ${GOLD}` }}>
                  <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200&q=80" alt="Testimonial" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div>
                  <div className="font-bold text-base" style={{ color: GOLD }}>{t.testimonialAuthor}</div>
                  <div className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>{t.testimonialContext}</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PACKAGES */}
        <section className="max-w-6xl mx-auto px-5 md:px-8 mt-20 md:mt-24">
          <div className="grid md:grid-cols-3 gap-6">
            {t.packages.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="relative rounded-3xl p-7 md:p-8 flex flex-col" style={{
                  background: p.highlight ? NAVY : '#fff',
                  color: p.highlight ? '#fff' : NAVY,
                  border: `2px solid ${p.highlight ? GOLD : '#E8E0D5'}`,
                  boxShadow: p.highlight ? '0 30px 80px -25px rgba(27,42,74,0.35)' : '0 12px 40px -20px rgba(27,42,74,0.15)'
                }} data-testid={`ss-package-${i}`}>
                  {p.highlight && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.2em] uppercase" style={{ background: GOLD, color: NAVY }}>Popular</div>}
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: p.highlight ? GOLD : NAVY }}>
                    <Icon className="w-6 h-6" style={{ color: p.highlight ? NAVY : GOLD }} />
                  </div>
                  <div className="font-display text-2xl md:text-3xl mb-1" style={{ fontWeight: 900 }}>{p.name}</div>
                  <div className="text-sm mb-5" style={{ color: p.highlight ? 'rgba(255,255,255,0.7)' : '#666' }}>{p.desc}</div>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="font-display text-4xl md:text-5xl" style={{ fontWeight: 900 }}>{p.price}</span>
                    <span className="text-sm" style={{ color: p.highlight ? 'rgba(255,255,255,0.7)' : '#666' }}>{p.period}</span>
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD }}>{t.included}</div>
                  <ul className="space-y-2.5 text-sm mb-7 flex-1">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex gap-2">
                        <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: GOLD }} />
                        <span style={{ color: p.highlight ? 'rgba(255,255,255,0.9)' : '#2C2C2C' }}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => setFormOpen(true)} data-testid={`ss-cta-${i}`} className="w-full h-12 rounded-full font-bold text-sm transition hover:opacity-90" style={{ background: p.highlight ? GOLD : NAVY, color: p.highlight ? NAVY : '#fff' }}>
                    {t.cta}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs mt-8" style={{ color: '#888' }}>{t.guarantee}</p>
        </section>
      </main>
      <Footer />
      <FloatingContact onOpenForm={() => setFormOpen(true)} />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
